import { useEffect, useState } from 'react';
import { color, chaseTheme, redcapRuleTheme } from '../styles/theme';
import { dateTime, num, plainDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import {
  useMeta,
} from '../api/queries';
import {
  ApiError,
  type RedcapImportResult,
  type RedcapRecord,
} from '../api/client';
import {
  useDictionary,
  useRedcapImport,
  useRedcapRecords,
  useRedcapSetup,
  useRedcapSummaries,
  useMatchCandidates,
  useRecordMatch,
  useCreateMatch,
  useBreakMatch,
  MATCH_METHODS,
  type MatchMethod,
} from '../api/redcap';
import { usePhiMatchAccess } from '../api/operations';
import { RedcapSetup } from './RedcapSetup';
import { RedcapExport } from './RedcapExport';
import { RedcapAnalytics } from './RedcapAnalytics';
import { RedcapWeek } from './RedcapWeek';
import { StepRail, suggestedTab, useRedcapStatus, type RedcapTab } from './RedcapSteps';
import { AboutPanel } from './MeetingBits';
import {
  Button,
  Card,
  CardTitle,
  ColHead,
  Empty,
  ErrorNote,
  Field,
  Loading,
  PageTitle,
  RoundButton,
  Tile,
  inputStyle,
  selectStyle,
} from '../components/ui';
import {
  FileButton,
  GuideCard,
  Note,
  Pill,
  PointerCard,
  ProblemNote,
  Where,
  problemOf,
  type Problem,
} from './RedcapShared';

/**
 * REDCap — what the dashboard may know about the study record, and nothing
 * more, laid out as the procedure it is.
 *
 * One rail of five steps (RedcapSteps.tsx) is the navigation: set the study
 * up once, then the three files a person carries between REDCap and here —
 * the Monday completeness counts in, accepted sign-ups out, de-identified
 * answers back — and the counts over what came back. Each tab opens with a
 * "How to do this" that names where every step happens, and a tab whose
 * prerequisite is missing says which step settles it rather than showing a
 * form that cannot work.
 *
 * The intake workflow (v1.0) draws the line the whole screen keeps: REDCap is
 * the only system allowed to hold clinical content, so the weekly file is
 * counts only, the record import refuses identifier columns, and the export
 * is logged as a PHI access before a byte leaves. Those refusals are named on
 * screen, column by column, so they read as the rule and not as a fault.
 */

export type { RedcapTab } from './RedcapSteps';
type Tab = RedcapTab;

/**
 * Where another screen asked this one to open: a study and a tab, and for the
 * Record import tab one record to land on. `nonce` makes a repeat ask count.
 */
export interface RedcapTarget {
  study: string;
  tab: RedcapTab;
  recordId?: string;
  nonce: number;
}

/** 8 MB, the API's limit for a record export. */
const RECORDS_MAX_BYTES = 8 * 1024 * 1024;

/* ═════════════════════════════════════════════════════════ record import */

/**
 * What an import left out, by reason. A full export of a big project leaves
 * out most of its columns — every form the study does not keep — so those
 * are counted per form rather than listed one by one.
 */
function leftOut(dropped: RedcapImportResult['droppedColumns']) {
  const forms = new Map<string, number>();
  const reasons = new Map<string, string[]>();
  for (const c of dropped) {
    const form = /^on the form (\S+), which this CRM does not keep$/.exec(c.why)?.[1];
    if (form) forms.set(form, (forms.get(form) ?? 0) + 1);
    else reasons.set(c.why, [...(reasons.get(c.why) ?? []), c.column]);
  }
  return { forms: [...forms.entries()], reasons: [...reasons.entries()] };
}

const SHOWN = 12;

function ImportResult({ r }: { r: RedcapImportResult }) {
  const out = leftOut(r.droppedColumns);
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as const;
  return (
    <div
      role="status"
      style={{
        marginTop: 14,
        background: '#cfe9d8',
        color: '#14563c',
        borderRadius: 14,
        padding: '12px 16px',
        fontSize: 13.5,
        fontWeight: 600,
      }}
    >
      Imported {num(r.rows)} {r.rows === 1 ? 'row' : 'rows'}: {num(r.inserted)} new, {num(r.updated)} updated,
      from {num(r.columns)} columns.
      {r.linkColumn
        ? ` ${num(r.suggested)} ${r.suggested === 1 ? 'record names' : 'records name'} a website inquiry in “${r.linkColumn}” — a suggestion to review, never a match.`
        : ''}
      {r.droppedColumns.length > 0 && (
        <>
          {' '}Left out {num(r.droppedColumns.length)} {r.droppedColumns.length === 1 ? 'column' : 'columns'} — the
          trial record stays in REDCap:
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {out.reasons.map(([why, columns]) => (
              <li key={why}>
                {why}:{' '}
                {columns.slice(0, SHOWN).map((c, i) => (
                  <span key={c}>
                    {i > 0 ? ', ' : ''}
                    <code style={mono}>{c}</code>
                  </span>
                ))}
                {columns.length > SHOWN ? ` and ${num(columns.length - SHOWN)} more` : ''}
              </li>
            ))}
            {out.forms.length > 0 && (
              <li>
                {num(out.forms.reduce((n, [, k]) => n + k, 0))} from {num(out.forms.length)}{' '}
                {out.forms.length === 1 ? 'form' : 'forms'} this study does not keep:{' '}
                {out.forms.slice(0, SHOWN).map(([form, k], i) => (
                  <span key={form}>
                    {i > 0 ? ', ' : ''}
                    <code style={mono}>{form}</code> ({num(k)})
                  </span>
                ))}
                {out.forms.length > SHOWN ? ` and ${num(out.forms.length - SHOWN)} more` : ''}
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}

const RECORD_GRID = 'minmax(0,1fr) minmax(0,1.1fr) minmax(0,0.9fr) minmax(0,1.5fr) minmax(0,1.1fr) minmax(0,0.8fr)';
const PAGE = 25;

interface MatchAccess {
  canView: boolean;
  canMatch: boolean;
}

const methodLabel = (key: string): string => MATCH_METHODS.find((m) => m.key === key)?.label ?? key;

/**
 * Linking one de-identified record to the sign-up it belongs to — the act that
 * creates PHI, so it needs PHI-Match "match", granted per person, and every
 * read of an existing link is logged. Nothing here loads until someone opens
 * it: a list of records must not become a list of PHI reads.
 */
function MatchPanel({ r, access }: { r: RedcapRecord; access: MatchAccess }) {
  const [finding, setFinding] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [method, setMethod] = useState<MatchMethod>('manual');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const link = useRecordMatch(r.matched && access.canView ? r.id : null);
  const candidates = useMatchCandidates(finding && access.canMatch && !r.matched ? r.id : null);
  const create = useCreateMatch();
  const breakIt = useBreakMatch();

  if (!access.canView && !access.canMatch) {
    return <Note>Linking a record to a sign-up needs PHI-Match access, which is granted per person.</Note>;
  }

  if (r.matched) {
    const m = link.data?.matches[0];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {link.isLoading && <Note>Loading the link…</Note>}
        {link.error && <ErrorNote message={(link.error as Error).message} />}
        {m && (
          <Note>
            Linked to website record <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{m.submissionId}</code>{' '}
            · {methodLabel(m.method)} · by {m.matchedByName} on {dateTime(m.matchedAt)}
            {m.note ? ` · “${m.note}”` : ''}
          </Note>
        )}
        {m && access.canMatch && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Why break this link?" style={{ flex: 1, minWidth: 240 }}>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="At least eight characters — an audit will read it"
                style={inputStyle}
              />
            </Field>
            <Button
              variant="danger"
              disabled={reason.trim().length < 8 || breakIt.isPending}
              onClick={() => breakIt.mutate({ id: m.id, reason: reason.trim() }, { onSuccess: () => setReason('') })}
            >
              {breakIt.isPending ? 'Breaking…' : 'Break link'}
            </Button>
          </div>
        )}
        {breakIt.error && <ProblemNote problem={problemOf(breakIt.error)} />}
      </div>
    );
  }

  if (!access.canMatch) return <Note>Not linked to a sign-up.</Note>;
  if (!finding) {
    return (
      <div>
        <Button variant="soft" onClick={() => setFinding(true)} style={{ padding: '7px 14px', fontSize: 12.5 }}>
          Find the sign-up it belongs to
        </Button>
      </div>
    );
  }

  const list = candidates.data?.candidates ?? [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {candidates.isLoading && <Note>Looking for sign-ups on this study…</Note>}
      {candidates.error && <ProblemNote problem={problemOf(candidates.error)} />}
      {candidates.data && (
        <Note>
          {candidates.data.basis === 'inquiry_id'
            ? 'The record names this inquiry ID.'
            : candidates.data.basis === 'study'
              ? 'The record names no inquiry ID, so these are the sign-ups on this study not yet linked. Confirm it is the same person before linking.'
              : 'No sign-up on this study is waiting to be linked.'}
        </Note>
      )}
      {list.map((c) => (
        <label
          key={c.submissionId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 10px',
            border: `1px solid ${chosen === c.submissionId ? color.blue : color.line}`,
            borderRadius: 12,
            background: color.white,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          <input
            type="radio"
            name={`match-${r.id}`}
            checked={chosen === c.submissionId}
            onChange={() => setChosen(c.submissionId)}
          />
          {/* The API leaves the name out for a role without PHI read. */}
          {c.name ? (
            <span style={{ fontWeight: 700, color: color.navy, flex: 1, minWidth: 0 }}>{c.name}</span>
          ) : (
            <span
              title="Names are shown only to roles that may see patient details"
              style={{ fontWeight: 600, color: color.muted, flex: 1, minWidth: 0 }}
            >
              Name hidden
            </span>
          )}
          <span style={{ color: color.muted, fontSize: 12 }}>
            {c.status} · {plainDate(c.createdAt)}
          </span>
        </label>
      ))}
      {list.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="How you know" style={{ width: 270 }}>
            <select value={method} onChange={(e) => setMethod(e.target.value as MatchMethod)} style={selectStyle}>
              {MATCH_METHODS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Note (optional)" style={{ flex: 1, minWidth: 200 }}>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} style={inputStyle} />
          </Field>
          <Button
            disabled={!chosen || create.isPending}
            onClick={() =>
              create.mutate(
                { submissionId: chosen!, redcapId: r.id, method, note: note.trim() || undefined },
                { onSuccess: () => setFinding(false) },
              )
            }
          >
            {create.isPending ? 'Linking…' : 'Link records'}
          </Button>
          <Button variant="ghost" onClick={() => setFinding(false)}>
            Cancel
          </Button>
        </div>
      )}
      {create.error && <ProblemNote problem={problemOf(create.error)} />}
    </div>
  );
}

function RecordRow({ r, access, defaultOpen = false }: { r: RedcapRecord; access: MatchAccess; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const fields = Object.entries(r.data ?? {});
  const show = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

  return (
    <div style={{ borderTop: `1px solid ${color.line}` }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="hov-tint"
        style={{
          display: 'grid',
          gridTemplateColumns: RECORD_GRID,
          gap: 12,
          alignItems: 'center',
          width: '100%',
          padding: '10px 16px',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 13,
          color: color.ink,
        }}
      >
        <span style={{ fontWeight: 700, color: color.navy, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
          {r.recordId}
        </span>
        <span style={{ color: color.muted }}>
          {/* A longitudinal record has a row per event; its fields below are each one's first answer. */}
          {r.rows > 1 ? `${num(r.rows)} rows` : (r.event ?? '—')}
        </span>
        <span style={{ color: color.muted }}>{r.dataAccessGroup ?? '—'}</span>
        <span style={{ color: color.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fields.length} {fields.length === 1 ? 'field' : 'fields'}
          {fields.length > 0 ? ` · ${fields.slice(0, 3).map(([k]) => k).join(', ')}${fields.length > 3 ? '…' : ''}` : ''}
        </span>
        <span style={{ color: color.muted, fontSize: 12 }}>{r.importedAt ? dateTime(r.importedAt) : '—'}</span>
        <span>
          <Pill tone={r.matched ? chaseTheme.done : redcapRuleTheme.none}>{r.matched ? 'Linked' : 'Not linked'}</Pill>
        </span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 16px 14px' }}>
          {fields.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
              {fields.map(([k, v]) => (
                <Tile key={k} label={k} value={show(v) || '—'} />
              ))}
            </div>
          )}
          <MatchPanel r={r} access={access} />
        </div>
      )}
    </div>
  );
}

function Records({
  slug,
  name,
  onGo,
  openRecordId,
}: {
  slug: string;
  name: string;
  onGo: (tab: Tab) => void;
  /** A record another page asked to land on: the list narrows to it, expanded. */
  openRecordId?: string | null;
}) {
  const { can } = useAuth();
  const canImport = can('redcap:import');
  const canRead = can('submissions:read');
  const doImport = useRedcapImport();
  const [result, setResult] = useState<RedcapImportResult | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [matched, setMatched] = useState<'yes' | 'no' | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [only, setOnly] = useState<string | null>(openRecordId ?? null);
  const records = useRedcapRecords({ study: slug, matched, limit: PAGE, offset, recordId: only ?? undefined }, canRead);
  const phi = usePhiMatchAccess();
  const access: MatchAccess = { canView: Boolean(phi.data?.canView), canMatch: Boolean(phi.data?.canMatch) };
  // The API refuses an import until the dictionary is on file and the forms
  // are chosen, and reads the record ID field from the study's project; say
  // which is missing before the button, and point at step 1.
  const setup = useRedcapSetup();
  const dict = useDictionary(slug);
  const missing: 'project' | 'dictionary' | 'forms' | null = !setup.data || !dict.data
    ? null
    : dict.data.project === null
      ? 'project'
      : setup.data.summary.fields === 0
        ? 'dictionary'
        : setup.data.setup.keptForms === null
          ? 'forms'
          : null;

  async function onFile(file: File) {
    setResult(null);
    setProblem(null);
    if (file.size > RECORDS_MAX_BYTES) {
      setProblem({ message: 'That file is larger than an export can be (8 MB).' });
      return;
    }
    try {
      setResult(await doImport.mutateAsync({ study: slug, csv: await file.text() }));
      setOffset(0);
    } catch (err) {
      setProblem(problemOf(err));
      // 409 `details.missing`: the project or the dictionary went since this
      // screen loaded. Its sentence shows; a fresh read brings the card that
      // points at step 1 back as well.
      if (err instanceof ApiError && err.status === 409 && (err.details as { missing?: unknown } | undefined)?.missing) {
        void setup.refetch();
        void dict.refetch();
      }
    }
  }

  const total = records.data?.total ?? 0;
  const filters: ReadonlyArray<[typeof matched, string]> = [
    [undefined, 'Every record'],
    ['yes', 'Linked'],
    ['no', 'Not linked'],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {missing && (
        <PointerCard
          title={
            missing === 'project'
              ? `Link ${name} to its REDCap project first`
              : missing === 'dictionary'
                ? 'Upload the data dictionary first'
                : 'Choose the forms the CRM keeps first'
          }
          actionLabel={canImport ? 'Go to Project setup' : undefined}
          onAction={canImport ? () => onGo('setup') : undefined}
        >
          {missing === 'project'
            ? 'An import is keyed on the project’s own record ID field and event names, which come with the link. Step 1, under Project setup.'
            : missing === 'dictionary'
              ? 'An import is read through the dictionary: it says which columns are identifiers, which are refused, and which form each column belongs to, which decides what is kept. Step 1, under Project setup.'
              : 'The import keeps only the forms chosen under Project setup, and is refused until they are chosen. Step 1.'}
        </PointerCard>
      )}
      <GuideCard
        storageKey="enact.redcap.guide.records"
        intro="REDCap’s de-identified export, back into the CRM: the recruitment and tracking answers from the forms kept, one row per record and event. Scores, labs, the treatment arm and anything with a name stay in REDCap."
        steps={[
          {
            do: 'Export de-identified data from REDCap.',
            where: (
              <Where at="REDCap">
                Data Exports, Reports, and Stats → All data → Export Data → CSV (raw). Under de-identification
                tick “Remove all tagged identifier fields”. Leave the record ID as it is — it is how rows are
                matched to records.
              </Where>
            ),
          },
          {
            do: 'Upload the file here.',
            where: (
              <Where at="here">
                Choose CSV. A file carrying a name, email, phone or date of birth is refused whole. Columns from
                forms not kept are left out and listed with the reason.
              </Where>
            ),
          },
          {
            do: 'Link records to their sign-ups.',
            where: (
              <Where at="here">
                open a record and choose “Find the sign-up it belongs to”. Linking needs PHI-Match access; the list
                shows only that a link exists, never to whom.
              </Where>
            ),
          },
          {
            do: 'Count the answers.',
            where: <Where at="here">step 5, Analytics, counts what was imported, read through the dictionary.</Where>,
          },
        ]}
      />
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <CardTitle style={{ marginBottom: 6 }}>Import a de-identified record export</CardTitle>
            <Note>
              The de-identified export for {name}: a row per record, or per record and event in a longitudinal
              project. Re-importing refreshes the same record and event and leaves its other events as they
              were. What was left out, and why, is listed after each import.
            </Note>
          </div>
          {canImport ? (
            <FileButton
              label={doImport.isPending ? 'Importing…' : 'Choose CSV'}
              disabled={doImport.isPending || missing !== null}
              onFile={onFile}
            />
          ) : (
            <Note>Importing needs the REDCap permission.</Note>
          )}
        </div>
        {result && <ImportResult r={result} />}
        {problem && (
          <div style={{ marginTop: 14 }}>
            <ProblemNote problem={problem} />
          </div>
        )}
      </Card>

      {canRead ? (
        <Card pad={0}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 16px 12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <CardTitle style={{ marginBottom: 4 }}>Imported records</CardTitle>
              <Note>{records.data ? `${num(total)} for ${name}.` : ' '} Whether a record is linked says only that a link exists — never to whom.</Note>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {only && (
                <Button variant="ghost" onClick={() => setOnly(null)} style={{ padding: '5px 12px', fontSize: 12 }}>
                  Record {only} · show every record
                </Button>
              )}
              {filters.map(([key, label]) => (
                <button
                  key={label}
                  onClick={() => {
                    setMatched(key);
                    setOffset(0);
                  }}
                  style={{
                    font: 'inherit',
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '5px 12px',
                    borderRadius: 999,
                    cursor: 'pointer',
                    border: `1px solid ${matched === key ? color.navy : color.line}`,
                    background: matched === key ? color.navy : color.white,
                    color: matched === key ? color.white : color.ink,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {records.isLoading && <Loading />}
          {records.error && (
            <div style={{ padding: '0 16px 16px' }}>
              <ErrorNote message={(records.error as Error).message} />
            </div>
          )}
          {records.data && total === 0 && (
            <p style={{ padding: '14px 16px 18px', margin: 0, fontSize: 13.5, color: color.muted }}>
              No records imported for {name} yet.
            </p>
          )}
          {records.data && total > 0 && (
            <div className="x-scroll">
              <div style={{ minWidth: 720 }}>
                <div style={{ display: 'grid', gridTemplateColumns: RECORD_GRID, gap: 12, padding: '0 16px 8px' }}>
                  <ColHead>Record</ColHead>
                  <ColHead>Event</ColHead>
                  <ColHead>Access group</ColHead>
                  <ColHead>Fields</ColHead>
                  <ColHead>Imported</ColHead>
                  <ColHead>Link</ColHead>
                </div>
                {records.data.records.map((r) => (
                  <RecordRow key={r.id} r={r} access={access} defaultOpen={only !== null && r.recordId === only} />
                ))}
              </div>
            </div>
          )}
          {records.data && total > PAGE && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '12px 16px', borderTop: `1px solid ${color.line}` }}>
              <span style={{ fontSize: 12.5, color: color.muted }}>
                {num(offset + 1)}–{num(Math.min(offset + PAGE, total))} of {num(total)}
              </span>
              <RoundButton title="Previous page" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                ‹
              </RoundButton>
              <RoundButton title="Next page" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
                ›
              </RoundButton>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          <Note>Listing imported records needs the permission to read submissions.</Note>
        </Card>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ page */


export function Redcap({
  target,
  onOpenBoard,
  onOpenMeeting,
  onGoMeetings,
}: {
  target?: RedcapTarget | null;
  /** Open a study's participant board. */
  onOpenBoard: (study: string) => void;
  /** Open one meeting, after a line was put on its agenda. */
  onOpenMeeting: (id: string) => void;
  /** Go to Meetings when no meeting is being prepared. */
  onGoMeetings: () => void;
}) {
  const meta = useMeta();
  const summaries = useRedcapSummaries();
  const { admin, can } = useAuth();
  // The dictionary, forms kept and map are every study's (043): changing them takes all of them.
  const canWriteSetup = can('redcap:import') && admin?.allStudies === true;
  const [picked, setPicked] = useState<string | null>(target?.study ?? null);
  /** The step chosen by hand, or by the board that sent us. Null until the status suggests one. */
  const [chosen, setChosen] = useState<Tab | null>(target?.tab ?? null);
  /** A record the Record import tab was asked to land on. */
  const [recordTarget, setRecordTarget] = useState<string | null>(target?.recordId ?? null);

  // Another screen (a study's board, the Dashboard) asked to open a study on a tab.
  useEffect(() => {
    if (!target) return;
    setPicked(target.study);
    setChosen(target.tab);
    setRecordTarget(target.recordId ?? null);
  }, [target]);

  const goTab = (tab: Tab) => {
    if (tab !== 'records') setRecordTarget(null);
    setChosen(tab);
  };

  const studies = meta.data?.studies ?? [];
  const configured = new Map((summaries.data?.studies ?? []).map((s) => [s.studySlug, s]));

  // The study picked, while it is still one this person may see; otherwise the
  // first with its project linked, otherwise the first there is.
  const slug =
    (picked && studies.some((s) => s.key === picked) ? picked : null) ??
    studies.find((s) => configured.has(s.key))?.key ??
    studies[0]?.key ??
    null;
  const study = studies.find((s) => s.key === slug);
  const name = study?.short || study?.label || slug || '';

  const status = useRedcapStatus(slug);
  // Land where the work is: setup until it is complete, then whatever is late, then the Monday ritual.
  const tab: Tab | null = chosen ?? (status.ready ? suggestedTab(status) : null);

  if (meta.isLoading) return <Loading />;
  if (meta.error) return <ErrorNote message={(meta.error as Error).message} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-in">
      <PageTitle sub="Each study’s REDCap project, step by step: set it up once, then move files by hand — the Monday status report in, accepted sign-ups out, de-identified answers back. The clinical record itself never leaves REDCap.">
        REDCap
      </PageTitle>

      <AboutPanel storageKey="enact.redcap.about" title="About REDCap">
        REDCap holds the clinical record; this dashboard holds only what recruitment needs, and the two meet only
        through a CSV a person carries. <strong>Project setup</strong> is done once: link the study’s project, upload
        the lab’s data dictionary, choose the forms the CRM keeps. Then three exchanges. Every Monday the{' '}
        <strong>This week</strong> file comes in — REDCap’s own report of every form’s status, with the calendar and the
        data-quality queries beside it when the lab exports them — and one page lists what to talk about: a form left
        incomplete two Mondays running is a <em>chase</em>, a task with an owner and a due date; a safety form behind, a
        visit missed, a query left open, a record stalled are flagged in the order to take them. Accepted sign-ups go out through{' '}
        <strong>Export to REDCap</strong>, a file for REDCap’s Data Import Tool. The de-identified answers come back
        through <strong>Record import</strong> and are counted in <strong>Analytics</strong>. A file carrying a name,
        contact detail, score or treatment arm is refused — that is the intake workflow’s boundary, not a fault in the
        file. In REDCap’s words an <em>event</em> is a visit and an <em>instrument</em> is a form; a participant is a
        record ID here, never a name.
      </AboutPanel>

      {studies.length === 0 ? (
        <Empty>No studies you can work in yet.</Empty>
      ) : (
        <>
          <Field label="Study" style={{ width: 320, maxWidth: '100%' }}>
            <select value={slug ?? ''} onChange={(e) => setPicked(e.target.value)} style={selectStyle}>
              {studies.map((s) => {
                const sum = configured.get(s.key);
                const extra = sum
                  ? sum.overdueChases > 0
                    ? ` · ${sum.overdueChases} overdue`
                    : sum.openChases > 0
                      ? ` · ${sum.openChases} open ${sum.openChases === 1 ? 'chase' : 'chases'}`
                      : ' · linked'
                  : '';
                return (
                  <option key={s.key} value={s.key}>
                    {s.short || s.label}
                    {extra}
                  </option>
                );
              })}
            </select>
          </Field>

          <StepRail status={status} tab={tab} onPick={goTab} />

          {slug && tab === null && <Loading />}
          {slug && tab === 'setup' && (
            <RedcapSetup key={slug} slug={slug} canWrite={can('redcap:import')} canWriteSetup={canWriteSetup} />
          )}
          {slug && tab === 'completeness' && (
            <RedcapWeek
              key={slug}
              slug={slug}
              name={name}
              onGo={goTab}
              onOpenBoard={() => onOpenBoard(slug)}
              onOpenMeeting={onOpenMeeting}
              onGoMeetings={onGoMeetings}
              onOpenRecord={(recordId) => {
                setRecordTarget(recordId);
                setChosen('records');
              }}
            />
          )}
          {slug && tab === 'export' && (
            <RedcapExport
              key={slug}
              slug={slug}
              name={name}
              canWriteSetup={canWriteSetup}
              canExport={can('submissions:export') && can('submissions:read_phi')}
              onGo={goTab}
            />
          )}
          {slug && tab === 'records' && (
            <Records key={`${slug}:${recordTarget ?? ''}`} slug={slug} name={name} onGo={goTab} openRecordId={recordTarget} />
          )}
          {slug && tab === 'analytics' && <RedcapAnalytics key={slug} slug={slug} onGo={goTab} />}
        </>
      )}
    </div>
  );
}
